import React, { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import apiClient from '../api/client';

/**
 * User interface matching backend User schema
 */
export interface User {
  id: string;
  email: string;
  role: 'Admin' | 'Manager' | 'Technician' | 'Staff';
  branches: string[];
  activeBranch: string;
  isActive: boolean;
}

/**
 * Authentication context state interface
 */
export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  updateUser: () => Promise<void>;
  clearError: () => void;
}

/**
 * Authentication Context
 * Provides user authentication state and methods throughout the application
 * Validates: Requirements 17.1
 */
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 * Manages user authentication state, login, logout, and token refresh
 */
const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Check if user is authenticated by verifying stored token
   * Fetches current user data from backend
   */
  const checkAuth = useCallback(async () => {
    const accessToken = localStorage.getItem('accessToken');
    
    if (!accessToken) {
      setIsLoading(false);
      return;
    }

    try {
      // Fetch current user data from backend
      const response = await apiClient.get('/auth/me');
      setUser(response.data);
      setError(null);
    } catch (err: any) {
      // Token is invalid or expired, clear it
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
      console.error('Authentication check failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Update user state with fresh data from backend
   * Used after operations that modify user data (e.g., branch switching)
   */
  const updateUser = useCallback(async () => {
    try {
      const response = await apiClient.get('/auth/me');
      setUser(response.data);
      setError(null);
    } catch (err: any) {
      console.error('Failed to update user:', err);
    }
  }, []);

  /**
   * Initialize authentication state on component mount
   */
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  /**
   * Listen for custom auth events to detect SSO login
   * This allows the AuthContext to update when tokens are set by SSO login
   */
  useEffect(() => {
    const handleAuthChange = () => {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken && !user) {
        // Token added but user not set - likely SSO login
        checkAuth();
      }
    };

    window.addEventListener('authChanged', handleAuthChange);
    return () => window.removeEventListener('authChanged', handleAuthChange);
  }, [user, checkAuth]);

  /**
   * Login user with email and password
   * Stores JWT tokens in localStorage
   * @param email - User email
   * @param password - User password
   */
  const login = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      // Call login endpoint
      const response = await apiClient.post('/auth/login', { email, password });
      const { accessToken, refreshToken, user: userData } = response.data;

      // Store tokens in localStorage
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);

      // Set user state
      setUser(userData);
      setError(null);
    } catch (err: any) {
      const errorMessage = err.message || 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout user
   * Clears JWT tokens from localStorage and redirects to 360Restoran
   */
  const logout = useCallback(() => {
    // Clear tokens from localStorage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');

    // Reset user state
    setUser(null);
    setError(null);

    // Redirect to 360Restoran login page
    window.location.href = 'https://360restoran.com/auth/cover-login';
  }, []);

  /**
   * Refresh JWT access token using refresh token
   * Updates stored tokens in localStorage
   */
  const refreshToken = async (): Promise<void> => {
    const storedRefreshToken = localStorage.getItem('refreshToken');

    if (!storedRefreshToken) {
      throw new Error('Refresh token bulunamadı');
    }

    try {
      // Call refresh token endpoint
      const response = await apiClient.post('/auth/refresh', {
        refreshToken: storedRefreshToken,
      });
      const { accessToken, refreshToken: newRefreshToken } = response.data;

      // Update tokens in localStorage
      localStorage.setItem('accessToken', accessToken);
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }

      setError(null);
    } catch (err: any) {
      const errorMessage = err.message || 'Token yenileme başarısız';
      setError(errorMessage);
      
      // If refresh fails, logout user
      logout();
      throw new Error(errorMessage);
    }
  };

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    logout,
    refreshToken,
    updateUser,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
